import {
  DatabasesDefaultDirections,
  DefaultDirections,
  GraphQLGlobalTypes,
  TablesDefaultDirections
} from '@tableau/data-catalog-client-components';
import * as _ from 'underscore';

import { CatalogContentType } from '../../../catalog/CatalogContentType';
import { SortTool } from '../../../common/react/components/SortTool';
import { Order } from '../../helpers/Order';
import { ContentSortOptions } from '../ContentSortOptions';
import { GetDisplayValue, OptionDefinition, OptionsDictionary } from './SortOptionDefinitions';

export const NoMessage: GetDisplayValue = () => '';

/**
 * Data Catalog doesn't use the sort dropdown, so we can ignore all the Messages strings
 * when configuring the Catalog sort options.
 */
function makeDropdownSortOption<K extends string>(params: { lowercaseKey: K }): OptionDefinition {
  const { lowercaseKey } = params;

  return {
    id: lowercaseKey,
    getMenuItemDisplayValue: NoMessage,
    subSortOptions: {
      'asc': {
        id: `${lowercaseKey}.asc`,
        field: lowercaseKey,
        ascending: true,
        getMenuItemDisplayValue: NoMessage,
        toOrderClauseArray: () => [Order.asc(lowercaseKey)],
      },
      'desc': {
        id: `${lowercaseKey}.desc`,
        field: lowercaseKey,
        ascending: false,
        getMenuItemDisplayValue: NoMessage,
        toOrderClauseArray: () => [Order.desc(lowercaseKey)],
      }
    }
  };
}

function makeContentSortOption<K extends string>(params: { lowercaseKey: K, direction: GraphQLGlobalTypes.OrderDirection }): SortTool.SortOption {
  return {
    field: params.lowercaseKey,
    locFn: NoMessage,
    direction: params.direction === GraphQLGlobalTypes.OrderDirection.ASC ? 'asc' : 'desc',
  };
}

function getDropdownSortOptionsForCatalogType<K extends string>(orderKeysObj: {[P in K]: unknown}): OptionsDictionary {
  const dict: {[s: string]: OptionDefinition} = {};
  for (const orderKey in orderKeysObj) {
    const lowercaseKey = orderKey.toLowerCase();
    dict[lowercaseKey] = makeDropdownSortOption({ lowercaseKey });
  }
  return dict;
}

function getContentSortOptionsForCatalogType<K extends string>(defaultDirectionsForOrderKeys: DefaultDirections<K>): _.Dictionary<SortTool.SortOption> {
  const dict: _.Dictionary<SortTool.SortOption> = {};
  for (const orderKey in defaultDirectionsForOrderKeys) {
    const direction = defaultDirectionsForOrderKeys[orderKey];
    const lowercaseKey = orderKey.toLowerCase();
    dict[lowercaseKey] = makeContentSortOption({ lowercaseKey, direction });
  }
  return dict;
}

export const databaseDropdownSortOptions = getDropdownSortOptionsForCatalogType(GraphQLGlobalTypes.DatabaseOrderField);
export const tableDropdownSortOptions = getDropdownSortOptionsForCatalogType(GraphQLGlobalTypes.DatabaseTableOrderField);

export const contentSortOptionsByCatalogContentType: Record<CatalogContentType, ContentSortOptions.SortOptionDictionary> = {
  databaseWithCrass: {}, //Empty to make the compiler happy. This is used from the DatabaseSortOptions.ts
  tableWithCrass: {}, //Empty to make the compiler happy. This is used from the TableSortOptions.ts
  database: getContentSortOptionsForCatalogType(DatabasesDefaultDirections),
  table: getContentSortOptionsForCatalogType(TablesDefaultDirections),
};
